import React, { CSSProperties, Ref } from 'react';
import { defined } from '@unifocus/js-utils';
import { PaddingType, Panel } from '../panel/Panel';
import { reactNodeIsOfType } from '../../../utils';

/**
 * {@docCategory GridPanel}
 */
export interface GridItemProps {
   /**
    * The column where the item is placed.
    */
   column?: number;

   /**
    * The row where the item is placed.
    */
   row?: number;

   /**
    * The number of columns to span.
    *
    * @defaultvalue 1
    */
   columnSpan?: number;

   /**
    * The number of rows to span.
    *
    * @defaultvalue 1
    */
   rowSpan?: number;

   /**
    * The justify setting for the cell.
    *
    * @defaultvalue start
    */
   justify?: 'start' | 'end' | 'center' | 'stretch';

   /**
    * The align setting for the cell.
    *
    * @defaultvalue start
    */
   align?: 'start' | 'end' | 'center' | 'stretch';

   /**
    * Sets a className on the grid item.
    */
   className?: string;

   /**
    * Sets styles on the grid item.
    */
   style?: CSSProperties;
}

/**
 * {@docCategory GridPanel}
 */
export interface GridColDef {
   justify?: 'start' | 'end' | 'center' | 'stretch';
   align?: 'start' | 'end' | 'center' | 'stretch';
}

export type ColDefArray = GridColDef[];

/**
 * {@docCategory GridPanel}
 */
export interface GridPanelProps {
   /**
    * Specifies the number of columns. If the value is an array of GridColDef then the number of
    * columns is set to the size of the array.
    *
    * @defaultvalue 1
    */
   columns?: number | ColDefArray;

   /**
    * The number of rows. If this is left off, then the number of rows will be determined by the
    * number of children that are added.
    */
   rows?: number;

   /**
    * The gap between the columns and rows.
    *
    * @defaultvalue medium
    */
   gap?: boolean | 'small' | 'medium' | 'large'

   /**
    * The gap between the columns.
    *
    * @defaultvalue medium
    */
   hgap?: boolean | 'small' | 'medium' | 'large'

   /**
    * The gap between the rows.
    *
    * @defaultvalue medium
    */
   vgap?: boolean | 'small' | 'medium' | 'large'

   /**
    * Determines if the component has a border around it.
    *
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * Determines if the panel has padding around the content.
    *
    * @defaultvalue false
    */
   padding?: boolean | 'small' | 'medium' | 'large';

   /**
    * Determines if the panel has left padding
    *
    * @defaultvalue false
    */
   paddingLeft?: PaddingType;

   /**
    * Determines if the panel has right padding
    *
    * @defaultvalue false
    */
   paddingRight?: PaddingType;

   /**
    * Determines if the panel has top padding
    *
    * @defaultvalue false
    */
   paddingTop?: PaddingType;

   /**
    * Determines if the panel has bottom padding
    *
    * @defaultvalue false
    */
   paddingBottom?: PaddingType;

   /**
    * Used to enable the built in overflow configuration for the content area.
    * @defaultvalue false
    */
   scroll?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width and height.
    *
    * @defaultvalue false
    */
   fill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width.
    *
    * @defaultvalue false
    */
   horizontalFill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers height.
    *
    * @defaultvalue false
    */
   verticalFill?: boolean;

   /**
    * The width of the panel.
    *
    * @defaultvalue 100%
    */
   width?: string | number;

   /**
    * The height of the panel.
    *
    * @defaultvalue 100%
    */
   height?: string | number;

   /**
    * The minimum width of the panel.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The mimimum height of the panel.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the panel.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the panel.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * Sets a className on the outer panel.
    */
   className?: string;

   /**
    * Sets styles on the outer panel.
    */
   style?: CSSProperties;

   /**
    * Sets the reference property
    */
   ref?: Ref<any>;

   /**
    * Sets the id of the element in the DOM.
    */
   id?: string;
}

export const GridItem = (props: React.PropsWithChildren<GridItemProps>) => {
   return (
      <div>{props.children}</div>
   );
};

const GAPS = {
   'small': 8,
   'medium': 16,
   'large': 32
};

const isGridItem = (item: React.ReactNode): boolean => {
   return reactNodeIsOfType(item, GridItem);
};

const getNumberColumns = (props: React.PropsWithChildren<GridPanelProps>) => {
   if (typeof props.columns === 'number') {
      return props.columns as number;
   }

   if (props.columns) {
      return (props.columns as ColDefArray).length;
   }

   return 0;
};

const applyGridItemPropsToChildren = (props: React.PropsWithChildren<GridPanelProps>) => {
   const numberColumns = getNumberColumns(props);
   const colDefs = typeof props.columns !== 'number' ? props.columns as ColDefArray : null;

   return React.Children.map(props.children, (child, index) => {
      const reactElement = child as React.ReactElement;
      const gridItem = isGridItem(reactElement) ? reactElement : null;
      const childToRender = gridItem ? reactElement.props.children : child;
      const { column, row, columnSpan, rowSpan, justify, align, className } = reactElement.props;
      const columnStart = column || (numberColumns > 0 ? (index % numberColumns) + 1 : 0);
      const rowStart = row || (numberColumns > 0 ? Math.floor(index / numberColumns) + 1 : 0);
      const alignSelf = align || (colDefs && colDefs[columnStart - 1].align ? colDefs[columnStart - 1].align : null);
      const justifySelf = justify || (colDefs && colDefs[columnStart - 1].justify ? colDefs[columnStart - 1].justify : null);

      const style: CSSProperties = Object.assign({ ...reactElement.props.style },
                                                 childToRender.props ? childToRender.props.style : {});

      if (columnStart) {
         style.gridColumnStart = columnStart;
      }

      if (rowStart) {
         style.gridRowStart = rowStart;
      }

      if (columnSpan) {
         style.gridColumnEnd = `span ${columnSpan}`;
      }

      if (rowSpan) {
         style.gridRowEnd = `span ${rowSpan}`;
      }

      if (alignSelf) {
         style.alignSelf = alignSelf;
      }

      if (justifySelf) {
         style.justifySelf = justifySelf;
      }

      return React.cloneElement(childToRender, { style: style, className: className });
   });
};

export const GridPanel: React.FC<React.PropsWithChildren<GridPanelProps>> = React.forwardRef<HTMLDivElement>(
   (props: React.PropsWithChildren<GridPanelProps>, ref: Ref<HTMLDivElement>) => {
      const hgap = defined(props.hgap) ? props.hgap : props.gap;
      const vgap = defined(props.vgap) ? props.vgap : props.gap;

      const horizontalFill = defined(props.horizontalFill) ? props.horizontalFill : defined(props.fill) ? props.fill : false;
      const verticalFill = defined(props.verticalFill) ? props.verticalFill : defined(props.fill) ? props.fill : false;

      const style: CSSProperties = {
         ...props.style
      };

      if (!verticalFill) {
         style.alignContent = 'start';
      }

      if (!horizontalFill) {
         style.justifyContent = 'start'
      }

      if (hgap === true) {
         style.columnGap = GAPS.medium;
      }
      else if (hgap) {
         style.columnGap = GAPS[hgap];
      }

      if (vgap === true) {
         style.rowGap = GAPS.medium;
      }
      else if (vgap) {
         style.rowGap = GAPS[vgap];
      }

      let classNames = 'uf-grid-panel';

      if (props.className) {
         classNames += ' ' + props.className;
      }

      return (
         <Panel
            className={classNames}
            border={props.border}
            padding={props.padding}
            paddingLeft={props.paddingLeft}
            paddingRight={props.paddingRight}
            paddingTop={props.paddingTop}
            paddingBottom={props.paddingBottom}
            scroll={props.scroll}
            width={props.width}
            height={props.height}
            minHeight={props.minHeight}
            minWidth={props.minWidth}
            maxHeight={props.maxHeight}
            maxWidth={props.maxWidth}
            verticalFill={verticalFill}
            horizontalFill={horizontalFill}
            style={style}
            ref={ref}
            id={props.id}
         >
            {applyGridItemPropsToChildren(props)}
         </Panel>
      );
   });
